import { useCallback } from "react";

import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";

import { getProjectMeta, setMeta } from "@synthesis/shared-services/project-gge/projectMetaController/client";
import type { ProjectMetaDto } from "@synthesis/shared-services/project-gge/projectMetaController/types";

const META_KEYS = ["engineering", "models"] as const;

type MetaKey = (typeof META_KEYS)[number];

type ModelMetaValue = {
  id: string;
  name: string;
};

export type ProjectMetaItem = {
  key: string;
  value: ModelMetaValue[] | string;
};

const projectMetaQueryKey = (projectId: string | null) => ["projectMeta", projectId] as const;

const parseModelValues = (value: string): ModelMetaValue[] => {
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.flatMap((item) => {
      if (typeof item !== "object" || item === null || !("id" in item) || typeof item.id !== "string") {
        return [];
      }

      return [
        {
          id: item.id,
          name: "name" in item && typeof item.name === "string" ? item.name : "",
        },
      ];
    });
  } catch {
    return [];
  }
};

const useProjectMeta = (projectId: string | null, config: Omit<UseQueryOptions<ProjectMetaDto[], Error>, "queryKey" | "queryFn"> = {}) => {
  const queryClient = useQueryClient();

  const { data: projectMeta, ...restQuery } = useQuery({
    queryKey: projectMetaQueryKey(projectId),
    queryFn: async () => {
      if (!projectId) {
        throw new Error("projectId is undefined");
      }

      return (await getProjectMeta({ path: { projectId } })).data;
    },
    enabled: !!projectId,
    ...config,
  });

  const mutationUpdateMeta = useMutation({
    mutationFn: async ({ key, value }: { key: MetaKey; value: ModelMetaValue[] }) =>
      (await setMeta({ path: { projectId: projectId!, key }, body: { key, value: JSON.stringify(value) } })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectMetaQueryKey(projectId) });
    },
  });

  const deleteModel = async (modelId: string, key: MetaKey) => {
    if (!projectMeta) {
      return;
    }

    const models = projectMeta.find((meta) => meta.key === key);
    if (!models) {
      return;
    }

    const modelsValues = parseModelValues(models.value);
    const filtered = modelsValues.filter((model) => model.id !== modelId);

    mutationUpdateMeta.mutate({ key, value: filtered });
  };

  const addModel = async (modelId: string, name: string, key: MetaKey) => {
    if (!projectMeta) {
      return;
    }

    const models = projectMeta.find((meta) => meta.key === key);
    const otherKey = META_KEYS.find((metaKey) => metaKey !== key);

    if (otherKey) {
      await deleteModel(modelId, otherKey);
    }

    const modelsValues = models ? parseModelValues(models.value) : [];
    if (modelsValues.some((model) => model.id === modelId)) {
      return;
    }

    modelsValues.push({ id: modelId, name });
    mutationUpdateMeta.mutate({ key, value: modelsValues });
  };

  const getMeta = useCallback(
    (modelId: string): ProjectMetaItem[] | undefined => {
      if (!projectMeta) {
        return [];
      }

      return projectMeta
        .filter((item) => META_KEYS.includes(item.key as MetaKey))
        .map((item) => {
          const values = parseModelValues(item.value);

          return {
            key: item.key,
            value: values.filter((value) => value.id === modelId),
          };
        });
    },
    [projectMeta],
  );

  const updateMetaVersion = async (modelId: string, newModelId: string) => {
    if (!projectMeta) {
      return;
    }

    let keyToUpdate: MetaKey | null = null;
    let updatedValues: ModelMetaValue[] = [];

    for (const meta of projectMeta) {
      if (!META_KEYS.includes(meta.key as MetaKey)) {
        continue;
      }

      const values = parseModelValues(meta.value);
      if (!values.some((item) => item.id === modelId)) {
        continue;
      }

      updatedValues = values.map((item) => (item.id === modelId ? { ...item, id: newModelId } : item));
      keyToUpdate = meta.key as MetaKey;
      break;
    }

    if (keyToUpdate) {
      mutationUpdateMeta.mutate({ key: keyToUpdate, value: updatedValues });
    }
  };

  return {
    projectMeta,
    addModel,
    deleteModel,
    getMeta,
    updateMetaVersion,
    ...restQuery,
  };
};

export default useProjectMeta;
